import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDriverMoney } from '../utils/driverPortalDayTotals';

function CurrencyTurnoverCell({ currencies }) {
  if (!currencies?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {currencies.map((row) => (
        <Box key={row.currency}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            <Box component="span" sx={{ color: 'success.main' }}>
              {formatDriverMoney(row.completed, row.currency)}
            </Box>
            {' / '}
            {formatDriverMoney(row.total, row.currency)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            ≈ {formatDriverMoney(row.remaining, row.currency)} remaining
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function SummaryCard({ title, bucket }) {
  if (!bucket) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {bucket.completed_job_count} of {bucket.job_count} jobs completed
        {bucket.jobs_without_price > 0
          ? ` · ${bucket.jobs_without_price} without price`
          : ''}
      </Typography>
      <CurrencyTurnoverCell currencies={bucket.currencies} />
    </Paper>
  );
}

export default function DriverRevenueReportSection({ report, from, to }) {
  const [expandedDriver, setExpandedDriver] = useState(false);

  if (!report) return null;

  const { grand_total: grand, daily_totals: dailyTotals, drivers } = report;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 1,
          color: 'primary.main',
          fontWeight: 'bold',
          textAlign: 'center',
          fontSize: { xs: '1.25rem', sm: '1.75rem' },
        }}
      >
        Driver revenue (job prices)
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', mb: 3, maxWidth: 720, mx: 'auto' }}
      >
        Same totals as the driver portal day summary: completed sell price / total scheduled
        price, and approximately remaining. Cancelled jobs are excluded.
        {from && to ? ` Period: ${from} to ${to}.` : ''}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <SummaryCard title="All drivers — period total" bucket={grand} />
      </Box>

      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, px: { xs: 1, sm: 0 } }}>
          Daily total (all drivers)
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Jobs
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Completed / Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyTotals?.length ? (
                dailyTotals.map((row) => (
                  <TableRow key={row.date} hover>
                    <TableCell>{row.date}</TableCell>
                    <TableCell align="right">
                      {row.completed_job_count}/{row.job_count}
                    </TableCell>
                    <TableCell>
                      <CurrencyTurnoverCell currencies={row.currencies} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No jobs in this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, px: { xs: 1, sm: 0 } }}>
          By driver (period)
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Driver</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Jobs
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Completed / Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers?.length ? (
                drivers.map((row) => (
                  <TableRow key={row.driver} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.driver}</TableCell>
                    <TableCell align="right">
                      {row.period.completed_job_count}/{row.period.job_count}
                    </TableCell>
                    <TableCell>
                      <CurrencyTurnoverCell currencies={row.period.currencies} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                    No driver jobs in this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {drivers?.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Daily breakdown per driver
          </Typography>
          {drivers.map((row) => (
            <Accordion
              key={row.driver}
              expanded={expandedDriver === row.driver}
              onChange={(_, isExpanded) => setExpandedDriver(isExpanded ? row.driver : false)}
              disableGutters
              sx={{ mb: 1, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                  <Typography sx={{ fontWeight: 700, flex: '1 1 auto' }}>{row.driver}</Typography>
                  <Chip
                    size="small"
                    label={`${row.period.completed_job_count}/${row.period.job_count} jobs`}
                    variant="outlined"
                  />
                  {row.period.currencies?.[0] ? (
                    <Typography variant="caption" color="text.secondary">
                      {formatDriverMoney(row.period.currencies[0].completed, row.period.currencies[0].currency)}
                      {' / '}
                      {formatDriverMoney(row.period.currencies[0].total, row.period.currencies[0].currency)}
                    </Typography>
                  ) : null}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {row.daily?.length ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">
                            Jobs
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Completed / Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {row.daily.map((day) => (
                          <TableRow key={day.date} hover>
                            <TableCell>{day.date}</TableCell>
                            <TableCell align="right">
                              {day.completed_job_count}/{day.job_count}
                            </TableCell>
                            <TableCell>
                              <CurrencyTurnoverCell currencies={day.currencies} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No jobs on any day in this period.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
